import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Button, 
  Typography, 
  CircularProgress, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  DialogTitle,
  Box,
  TextField,
  TablePagination,
  InputAdornment
} from '@mui/material';
import { Alert } from '@mui/material';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';
import PageLoadingSpinner from '../../components/PageLoadingSpinner';
import PolicyIcon from '@mui/icons-material/Policy';
import SearchIcon from '@mui/icons-material/Search';
import Papa from 'papaparse';
import getUser from "../../getUser";
import validator from 'validator';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../components/Toast';
import { useDocumentTitle } from '../../hooks';
import { getCognitoGroups } from '../../auth/authUtils';
import { formatDateTime } from '../../i18n/formatters';

// Define types for watchlist items
interface WatchlistItem {
  orgNumber: string;
  orgName: string;
  addedAt: string;
}

const UploadWatchlistPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orgNumbers, setOrgNumbers] = useState<string[]>([]);
  const [allWatchlistItems, setAllWatchlistItems] = useState<WatchlistItem[]>([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedOrgNumber, setSelectedOrgNumber] = useState<string | null>(null);
  const [searchOrgNumber, setSearchOrgNumber] = useState<string>('');
  const [searchResult, setSearchResult] = useState<any | null>(null);
  const [bulkInsertFile, setBulkInsertFile] = useState<File | null>(null);
  const [bulkDeleteFile, setBulkDeleteFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [duplicateInfo, setDuplicateInfo] = useState<{type: 'insert' | 'delete', count: number} | null>(null);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deleteAllConfirmText, setDeleteAllConfirmText] = useState('');
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);
  const toast = useToast();
  useDocumentTitle(t('watchlist.documentTitle'));

  // Memoize auth token to avoid repeated calls
  const authToken = useMemo(() => getUser()?.access_token || null, []);

  const isListViewDisabled = useMemo(() => {
    const user = getUser();
    const groups = getCognitoGroups(user?.access_token || '');
    return groups.includes('Feature_DisableViewLists');
  }, []);

  // Memoize lowercase search term to avoid repeated calculations
  const lowerSearchTerm = useMemo(() => searchTerm.toLowerCase(), [searchTerm]);

  // Function to chunk array into smaller arrays
  const chunkArray = <T,>(array: T[], chunkSize: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  };

  const fetchAllWatchlistItems = useCallback(async (): Promise<WatchlistItem[]> => {
    if (!authToken) {
      throw new Error(t('watchlist.errors.unauthenticated'));
    }

    let allItems: WatchlistItem[] = [];
    let cursor = null;
    const limit = 100;
    let hasMore = true;

    while (hasMore) {
      const url = new URL(`${process.env.REACT_APP_API_ENDPOINT}/v1/lists/watchlist`);
      url.searchParams.append('limit', limit.toString());
      if (cursor) {
        url.searchParams.append('cursor', cursor);
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(t('watchlist.errors.fetchWithDetails', { details: await response.text() }));
      }

      const data = await response.json();
      allItems = [...allItems, ...data.orgNumbers];
      
      // Check if there are more items to fetch
      hasMore = data.hasRemaining;
      cursor = data.cursor;
    }

    return allItems;
  }, [authToken, t]);

  const fetchWatchlist = useCallback(async () => {
    if (isListViewDisabled) return;
    
    setLoading(true);
    try {
      if (!authToken) {
        setError(t('watchlist.errors.unauthenticated'));
        setLoading(false);
        return;
      }

      // Fetch ALL watchlist items
      const allItems = await fetchAllWatchlistItems();
      const formattedData = allItems.map((org: WatchlistItem) => ({
        ...org,
        addedAt: formatDateTime(org.addedAt, i18n.language) ?? '',
      }));
      
      setAllWatchlistItems(formattedData);
    } catch (err) {
      setError(t('watchlist.errors.fetch'));
    } finally {
      setLoading(false);
    }
  }, [authToken, fetchAllWatchlistItems, i18n.language, isListViewDisabled, t]);

  useEffect(() => {
    fetchWatchlist();
  }, [fetchWatchlist]);

  // Filter and paginate watchlist items
  const filteredWatchlistItems = useMemo(() => {
    return allWatchlistItems.filter(item => {
      // Global search filter
      const matchesGlobalSearch = !lowerSearchTerm || (
        item.orgNumber.toLowerCase().includes(lowerSearchTerm) ||
        item.orgName.toLowerCase().includes(lowerSearchTerm) ||
        item.addedAt.toLowerCase().includes(lowerSearchTerm)
      );

      return matchesGlobalSearch;
    });
  }, [allWatchlistItems, lowerSearchTerm]);

  const paginatedWatchlistItems = useMemo(() => {
    return filteredWatchlistItems.slice(
      currentPage * rowsPerPage,
      currentPage * rowsPerPage + rowsPerPage
    );
  }, [filteredWatchlistItems, currentPage, rowsPerPage]);

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setCurrentPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  }, []);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(0); // Reset to first page when searching
  }, []);


  const handleBulkInsertFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      if (!file.name.endsWith('.csv')) {
        setError(t('watchlist.invalidCsv'));
        return;
      }

      setBulkInsertFile(file);

      Papa.parse(file, {
        header: false, // Do not treat the first row as headers
        skipEmptyLines: true,
        complete: (results: { data: any[]; }) => {
          const dataRows = results.data.slice(1); // Skip the first row
          const parsedOrgNumbers = dataRows.map((row: any) => row[0]).filter((value: string) => validator.isNumeric(value)); // Validate numeric orgNumbers
          
          // Remove duplicates using Set
          const uniqueOrgNumbers = Array.from(new Set(parsedOrgNumbers));
          
          setOrgNumbers(uniqueOrgNumbers);
          setFilePreview(dataRows.slice(0, 15)); // Store the first 15 rows for preview
          
          // Show duplicate info if any were found
          if (parsedOrgNumbers.length !== uniqueOrgNumbers.length) {
            const duplicateCount = parsedOrgNumbers.length - uniqueOrgNumbers.length;
            setDuplicateInfo({ type: 'insert', count: duplicateCount });
            console.log(`Removed ${duplicateCount} duplicate organization numbers from upload file.`);
          } else {
            setDuplicateInfo(null);
          }
        },
        error: () => {
          setError(t('watchlist.errors.parseCsv'));
        },
      });
    } else {
      setBulkInsertFile(null);
      setFilePreview([]); // Clear preview if no file is selected
      setDuplicateInfo(null);
    }
  };

    const handleBulkDeleteFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setBulkDeleteFile(event.target.files[0]);

      Papa.parse(event.target.files[0], {
        header: false, // Do not treat the first row as headers
        skipEmptyLines: true,
        complete: (results: { data: any[]; }) => {
          const dataRows = results.data.slice(1); // Skip the first row
          const parsedOrgNumbers = dataRows.map((row: any) => row[0]).filter(Boolean); // Use the first column for orgNumbers
          
          // Remove duplicates using Set
          const uniqueOrgNumbers = Array.from(new Set(parsedOrgNumbers));
          
          setOrgNumbers(uniqueOrgNumbers);
          setFilePreview(dataRows.slice(0, 15)); // Store the first 15 rows for preview
          
          // Show duplicate info if any were found
          if (parsedOrgNumbers.length !== uniqueOrgNumbers.length) {
            const duplicateCount = parsedOrgNumbers.length - uniqueOrgNumbers.length;
            setDuplicateInfo({ type: 'delete', count: duplicateCount });
            console.log(`Removed ${duplicateCount} duplicate organization numbers from deletion file.`);
          } else {
            setDuplicateInfo(null);
          }
        },
        error: () => {
          setError(t('watchlist.errors.parseCsv'));
        },
      });
    } else {
      setBulkDeleteFile(null);
      setFilePreview([]); // Clear preview if no file is selected
      setDuplicateInfo(null);
    }
  };

  const handleSubmitInsert = async () => {
    if (!bulkInsertFile) {
      setError(t('watchlist.errors.selectInsertFile'));
      return;
    }

    if (orgNumbers.length === 0) {
      setError(t('watchlist.errors.noValidInsertRows'));
      return;
    }

    setError(null);
    setLoading(true);

    try {
      if (!authToken) {
        setError(t('watchlist.errors.unauthenticated'));
        setLoading(false);
        return;
      }

      // Additional duplicate removal as safety measure before API call
      const uniqueOrgNumbers = Array.from(new Set(orgNumbers));
      
      // Split the org numbers into chunks of 100 for insertion
      const chunks = chunkArray(uniqueOrgNumbers, 100);
      
      let successfulBatches = 0;
      let failedBatches = 0;
      const errors: string[] = [];

      // Initialize progress tracking
      setUploadProgress({ current: 0, total: chunks.length });

      // Process each chunk sequentially
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        
        // Update progress
        setUploadProgress({ current: i + 1, total: chunks.length });
        
        try {
          const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/v1/lists/watchlist`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${authToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ orgNumbers: chunk }),
          });

          if (!response.ok) {
            const errorMessage = await response.text();
            errors.push(t('watchlist.errors.batchWithDetails', { current: i + 1, total: chunks.length, details: errorMessage }));
            failedBatches++;
          } else {
            successfulBatches++;
          }
        } catch (batchError) {
          errors.push(t('watchlist.errors.batchNetwork', { current: i + 1, total: chunks.length }));
          failedBatches++;
        }
      }

      // Clear progress
      setUploadProgress(null);

      // Show results
      if (failedBatches === 0) {
        toast.success(t('watchlist.messages.insertSuccess', { count: uniqueOrgNumbers.length }));
        fetchWatchlist();
        setBulkInsertFile(null);
        setFilePreview([]);
        setOrgNumbers([]);
        setDuplicateInfo(null);
      } else if (successfulBatches > 0) {
        const errorDetails = errors.join('\n');
        setError(t('watchlist.errors.partialSuccess', { successful: successfulBatches, failed: failedBatches, details: errorDetails }));
        fetchWatchlist();
      } else {
        const errorDetails = errors.join('\n');
        setError(t('watchlist.errors.allBatchesFailed', { details: errorDetails }));
      }
    } catch (err) {
      setError(t('watchlist.errors.insert'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitDelete = async () => {
    if (!bulkDeleteFile) {
      setError(t('watchlist.errors.selectDeleteFile'));
      return;
    }

    if (orgNumbers.length === 0) {
      setError(t('watchlist.errors.noValidDeleteRows'));
      return;
    }

    setError(null);
    setLoading(true);

    try {
      if (!authToken) {
        setError(t('watchlist.errors.unauthenticated'));
        setLoading(false);
        return;
      }

      // Additional duplicate removal as safety measure before API call
      const uniqueOrgNumbers = Array.from(new Set(orgNumbers));
      
      // Split the org numbers into chunks of 100 for deletion
      const chunks = chunkArray(uniqueOrgNumbers, 100);
      
      let successfulBatches = 0;
      let failedBatches = 0;
      const errors: string[] = [];

      // Initialize progress tracking
      setUploadProgress({ current: 0, total: chunks.length });

      // Process each chunk sequentially
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        
        // Update progress
        setUploadProgress({ current: i + 1, total: chunks.length });
        
        try {
          const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/v1/lists/watchlist/delete`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${authToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ orgNumbers: chunk }),
          });

          if (!response.ok) {
            const errorMessage = await response.text();
            errors.push(t('watchlist.errors.batchWithDetails', { current: i + 1, total: chunks.length, details: errorMessage }));
            failedBatches++;
          } else {
            successfulBatches++;
          }
        } catch (batchError) {
          errors.push(t('watchlist.errors.batchNetwork', { current: i + 1, total: chunks.length }));
          failedBatches++;
        }
      }

      // Clear progress
      setUploadProgress(null);

      // Show results
      if (failedBatches === 0) {
        toast.success(t('watchlist.messages.deleteSuccess', { count: uniqueOrgNumbers.length }));
        fetchWatchlist();
        setBulkDeleteFile(null);
        setFilePreview([]);
        setOrgNumbers([]);
        setDuplicateInfo(null);
      } else if (successfulBatches > 0) {
        const errorDetails = errors.join('\n');
        setError(t('watchlist.errors.partialSuccess', { successful: successfulBatches, failed: failedBatches, details: errorDetails }));
        fetchWatchlist();
      } else {
        const errorDetails = errors.join('\n');
        setError(t('watchlist.errors.allBatchesFailed', { details: errorDetails }));
      }
    } catch (err) {
      setError(t('watchlist.errors.bulkDelete'));
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (orgNumber: string) => {
    setError(null);
    setLoading(true);

    try {
      if (!authToken) {
        setError(t('watchlist.errors.unauthenticated'));
        setLoading(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/v1/lists/watchlist/delete`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orgNumbers: [orgNumber] }),
      });

      if (!response.ok) {
        const errorMessage = await response.text();
        setError(t('watchlist.errors.removeWithDetails', { details: errorMessage }));
      } else {
        toast.success(t('watchlist.messages.removeSuccess', { orgNumber }));
        fetchWatchlist();
        setSearchResult(null); // Reset the search result table
      }
    } catch (err) {
      setError(t('watchlist.errors.remove'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAll = async () => {
    setError(null);
    setLoading(true);

    try {
      if (!authToken) {
        setError(t('watchlist.errors.unauthenticated'));
        setLoading(false);
        return;
      }

      // First, fetch ALL watchlist items
      const allWatchlistData = allWatchlistItems.length > 0 ? allWatchlistItems : await fetchAllWatchlistItems();
      
      if (allWatchlistData.length === 0) {
        toast.warning(t('watchlist.messages.nothingToDelete'));
        setLoading(false);
        setDeleteAllDialogOpen(false);
        setDeleteAllConfirmText('');
        return;
      }

      const allOrgNumbers = allWatchlistData.map(item => item.orgNumber);
      
      // Split the org numbers into chunks of 100 for deletion
      const chunks = chunkArray(allOrgNumbers, 100);
      
      let successfulBatches = 0;
      let failedBatches = 0;
      const errors: string[] = [];

      // Initialize progress tracking
      setUploadProgress({ current: 0, total: chunks.length });

      // Process each chunk sequentially
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        
        // Update progress
        setUploadProgress({ current: i + 1, total: chunks.length });
        
        try {
          const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/v1/lists/watchlist/delete`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${authToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ orgNumbers: chunk }),
          });

          if (!response.ok) {
            const errorMessage = await response.text();
            errors.push(t('watchlist.errors.batchWithDetails', { current: i + 1, total: chunks.length, details: errorMessage }));
            failedBatches++;
          } else {
            successfulBatches++;
          }
        } catch (batchError) {
          errors.push(t('watchlist.errors.batchNetwork', { current: i + 1, total: chunks.length }));
          failedBatches++;
        }
      }

      // Clear progress
      setUploadProgress(null);

      // Show results
      if (failedBatches === 0) {
        toast.success(t('watchlist.messages.deleteAllSuccess', { count: allWatchlistData.length }));
        fetchWatchlist();
        setDeleteAllDialogOpen(false);
        setDeleteAllConfirmText('');
      } else if (successfulBatches > 0) {
        const errorDetails = errors.join('\n');
        setError(t('watchlist.errors.partialSuccess', { successful: successfulBatches, failed: failedBatches, details: errorDetails }));
        fetchWatchlist();
        setDeleteAllDialogOpen(false);
        setDeleteAllConfirmText('');
      } else {
        const errorDetails = errors.join('\n');
        setError(t('watchlist.errors.allBatchesFailed', { details: errorDetails }));
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('watchlist.errors.deleteAll');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const openConfirmDialog = (orgNumber: string) => {
    setSelectedOrgNumber(orgNumber);
    setConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    setSelectedOrgNumber(null);
    setConfirmDialogOpen(false);
  };

  const openDeleteAllDialog = () => {
    setDeleteAllDialogOpen(true);
    setDeleteAllConfirmText('');
  };

  const closeDeleteAllDialog = () => {
    setDeleteAllDialogOpen(false);
    setDeleteAllConfirmText('');
  };

  const handleSearch = () => {
    if (!validator.isNumeric(searchOrgNumber)) {
      setError(t('watchlist.errors.invalidOrgNumber'));
      return;
    }

    const result = allWatchlistItems.find(org => org.orgNumber === searchOrgNumber);
    if (result) {
      setSearchResult(result);
    } else {
      setSearchResult(null);
      setError(t('watchlist.errors.notFound'));
    }
  };

  return (
    <Box sx={{ width: '100%', minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', '& > div': { maxWidth: '100%' }, '& > div:has(> input[type="file"])': { flexWrap: 'wrap' } }}>
      <PageHeader title={t('watchlist.title')} description={t('watchlist.description')} icon={<PolicyIcon />} />
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ width: '100%', mb: 2 }}>{error}</Alert>}

      {!isListViewDisabled && (
        <>
          <Typography variant="h6" gutterBottom>
            {t('watchlist.searchOrgNumber')}
          </Typography>
          <Box component="form" onSubmit={(event) => { event.preventDefault(); if (!loading && searchOrgNumber.trim()) handleSearch(); }} sx={{ mb: 2, width: '100%', maxWidth: 600, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <TextField
              size="small"
              label={t('watchlist.enterOrgNumber')}
              value={searchOrgNumber}
              onChange={(e) => setSearchOrgNumber(e.target.value)}
              disabled={loading}
              sx={{ flex: '1 1 220px', minWidth: 0 }}
            />
            <Button variant="contained" type="submit" disabled={loading || !searchOrgNumber.trim()}>
              {t('common.actions.search')}
            </Button>
          </Box>

          {searchResult && (
            <TableContainer component={Paper} style={{ marginTop: 'var(--spacing-md)', width: '100%', maxWidth: '800px' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('watchlist.columns.orgNumber')}</TableCell>
                    <TableCell>{t('watchlist.columns.orgName')}</TableCell>
                    <TableCell>{t('watchlist.columns.addedAt')}</TableCell>
                    <TableCell>{t('common.fields.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>{searchResult.orgNumber}</TableCell>
                    <TableCell>{searchResult.orgName}</TableCell>
                    <TableCell>{searchResult.addedAt}</TableCell>
                    <TableCell>
                      <Button
                        variant="text"
                        color="error"
                        onClick={() => openConfirmDialog(searchResult.orgNumber)}
                      >
                        {t('common.actions.remove')}
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Typography variant="h6" gutterBottom style={{ marginTop: 'var(--spacing-xl)' }}>
            {t('watchlist.current', { count: allWatchlistItems.length })}
          </Typography>
          
          <Box sx={{ width: '100%', maxWidth: '800px', mb: 2 }}>
            <TextField
              variant="outlined"
              placeholder={t('watchlist.searchPlaceholder')}
              size="small"
              inputProps={{ 'aria-label': t('watchlist.searchPlaceholder') }}
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: '100%' }}
            />
          </Box>

          <Box sx={{ width: '100%', maxWidth: '800px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
            {allWatchlistItems.length > 0 && (
              <Button 
                variant="outlined" 
                color="error" 
                onClick={openDeleteAllDialog}
                disabled={loading}
                size="small"
              >
                {t('watchlist.deleteAllWithCount', { count: allWatchlistItems.length })}
              </Button>
            )}
          </Box>
        </>
      )}

      {loading ? (
        <PageLoadingSpinner variant="inline" label={t('common.states.loading')} />
      ) : (
        !isListViewDisabled && (
          <>
            <TableContainer component={Paper} style={{ marginTop: 'var(--spacing-md)', width: '100%', maxWidth: '800px' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('watchlist.columns.orgNumber')}</TableCell>
                    <TableCell>{t('watchlist.columns.orgName')}</TableCell>
                    <TableCell>{t('watchlist.columns.addedAt')}</TableCell>
                    <TableCell>{t('common.fields.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedWatchlistItems.length > 0 ? (
                    paginatedWatchlistItems.map((org, index) => (
                      <TableRow key={`${org.orgNumber}-${index}`} hover>
                        <TableCell>{org.orgNumber}</TableCell>
                        <TableCell>{org.orgName}</TableCell>
                        <TableCell>{org.addedAt}</TableCell>
                        <TableCell>
                          <Button
                            variant="text"
                            color="error"
                            onClick={() => openConfirmDialog(org.orgNumber)}
                          >
                            {t('common.actions.remove')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <EmptyState title={searchTerm ? t('watchlist.noMatches', { searchTerm }) : t('watchlist.empty')} icon={<PolicyIcon />} sx={{ py: 4 }} />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {allWatchlistItems.length > 0 && (
              <TablePagination
                component="div"
                count={filteredWatchlistItems.length}
                page={currentPage}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{ width: '100%', maxWidth: '800px' }}
              />
            )}
          </>
        )
      )}

      <Dialog
        open={confirmDialogOpen}
        onClose={closeConfirmDialog}
      >
        <DialogTitle>{t('watchlist.confirmRemoval.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('watchlist.confirmRemoval.body', { orgNumber: selectedOrgNumber })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog} color="primary">
            {t('common.actions.cancel')}
          </Button>
          <Button
            onClick={() => {
              if (selectedOrgNumber) handleRemove(selectedOrgNumber);
              closeConfirmDialog();
            }}
            color="secondary"
          >
            {t('common.actions.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Typography variant="h6" gutterBottom style={{ marginTop: 'var(--spacing-md)' }}>
        {t('watchlist.instructions.title')}
      </Typography>
      <Typography variant="body2" component="div" color="text.secondary" gutterBottom style={{ marginBottom: 'var(--spacing-md)' }}>
        {t('watchlist.instructions.intro')}
        <ul>
          <li>{t('watchlist.instructions.header')}</li>
          <li>{t('watchlist.instructions.numeric')}</li>
          <li>{t('watchlist.instructions.validRows')}</li>
          <li>{t('watchlist.instructions.duplicates')}</li>
        </ul>
      </Typography>

      <Typography variant="h6" gutterBottom style={{ marginTop: 'var(--spacing-xl)' }}>
        {t('watchlist.bulkInsert')}
      </Typography>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)', marginBottom: 'var(--spacing-md)' }}>
        <input
          accept=".csv"
          style={{ display: 'none' }}
          id="bulk-insert-file"
          type="file"
          onChange={handleBulkInsertFileChange}
        />
          <Button variant="outlined" disabled={loading} onClick={() => document.getElementById('bulk-insert-file')?.click()}>
            {t('watchlist.chooseFile')}
          </Button>
        {bulkInsertFile && <Typography variant="body1">{t('watchlist.selectedFile', { name: bulkInsertFile.name })}</Typography>}
        {orgNumbers.length > 0 && (
          <Typography variant="body2" color="textSecondary">
            {t('watchlist.validEntries', { count: orgNumbers.length })}
          </Typography>
        )}
        {duplicateInfo && duplicateInfo.type === 'insert' && (
          <Typography variant="body2" color="warning.main">
            {t('watchlist.duplicatesRemoved', { count: duplicateInfo.count })}
          </Typography>
        )}
        {uploadProgress && (
          <Typography variant="body2" color="primary">
            {t('watchlist.uploadingBatch', uploadProgress)}
          </Typography>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmitInsert}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : t('common.actions.submit')}
        </Button>
      </div>

      {bulkInsertFile && filePreview.length > 0 && (
        <TableContainer component={Paper} style={{ marginTop: 'var(--spacing-md)' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('watchlist.columns.orgNumber')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filePreview.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  <TableCell>{row[0]}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!isListViewDisabled && (
        <>
          <Typography variant="h6" gutterBottom style={{ marginTop: 'var(--spacing-xl)' }}>
            {t('watchlist.bulkDelete')}
          </Typography>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)', marginBottom: 'var(--spacing-md)' }}>
            <input
              accept=".csv"
              style={{ display: 'none' }}
              id="bulk-delete-file"
              type="file"
              onChange={handleBulkDeleteFileChange}
            />
              <Button variant="outlined" disabled={loading} onClick={() => document.getElementById('bulk-delete-file')?.click()}>
                {t('watchlist.chooseFile')}
              </Button>
            {bulkDeleteFile && <Typography variant="body1">{t('watchlist.selectedFile', { name: bulkDeleteFile.name })}</Typography>}
            {orgNumbers.length > 0 && (
              <Typography variant="body2" color="textSecondary">
                {t('watchlist.validEntries', { count: orgNumbers.length })}
              </Typography>
            )}
            {duplicateInfo && duplicateInfo.type === 'delete' && (
              <Typography variant="body2" color="warning.main">
                {t('watchlist.duplicatesRemoved', { count: duplicateInfo.count })}
              </Typography>
            )}
            {uploadProgress && (
              <Typography variant="body2" color="primary">
                {t('watchlist.deletingBatch', uploadProgress)}
              </Typography>
            )}
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSubmitDelete}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : t('common.actions.submit')}
            </Button>
          </div>

          {bulkDeleteFile && filePreview.length > 0 && (
            <TableContainer component={Paper} style={{ marginTop: 'var(--spacing-md)' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('watchlist.columns.orgNumber')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filePreview.map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      <TableCell>{row[0]}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <Dialog
        open={deleteAllDialogOpen}
        onClose={closeDeleteAllDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>{t('watchlist.deleteAllDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('watchlist.deleteAllDialog.warning', { count: allWatchlistItems.length })}
          </DialogContentText>
          <DialogContentText sx={{ mb: 2, fontSize: '0.875rem', color: 'text.secondary' }}>
            {t('watchlist.deleteAllDialog.note')}
          </DialogContentText>
          <DialogContentText sx={{ mb: 3 }}>
            {t('watchlist.deleteAllPrompt', { phrase: t('watchlist.deleteAllPhrase') })}
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            variant="outlined"
            placeholder={t('watchlist.deleteAllDialog.placeholder', { phrase: t('watchlist.deleteAllPhrase') })}
            value={deleteAllConfirmText}
            onChange={(e) => setDeleteAllConfirmText(e.target.value)}
            error={deleteAllConfirmText.length > 0 && deleteAllConfirmText !== t('watchlist.deleteAllPhrase')}
            helperText={deleteAllConfirmText.length > 0 && deleteAllConfirmText !== t('watchlist.deleteAllPhrase') ? t('watchlist.deleteAllHelp', { phrase: t('watchlist.deleteAllPhrase') }) : ''}
          />
          {uploadProgress && (
            <Typography variant="body2" color="primary" sx={{ mt: 2 }}>
              {t('watchlist.deletingBatch', uploadProgress)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteAllDialog} color="primary">
            {t('common.actions.cancel')}
          </Button>
          <Button
            onClick={handleDeleteAll}
            color="error"
            variant="contained"
            disabled={deleteAllConfirmText !== t('watchlist.deleteAllPhrase') || loading}
          >
            {loading ? <CircularProgress size={20} /> : t('watchlist.deleteAll')}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default UploadWatchlistPage;
